import { motion } from "framer-motion";
import { Clock, Check, AlertCircle, Calendar, Package, TrendingUp } from "lucide-react";
import { api } from "../lib/api";

const STATUS_COLORS = {
  new: "bg-[#00D4FF]/10 text-[#00D4FF] border-[#00D4FF]/25",
  reviewing: "bg-[#8B2FE8]/10 text-[#8B2FE8] border-[#8B2FE8]/25",
  in_progress: "bg-amber-500/10 text-amber-600 border-amber-500/20",
  completed: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
  rejected: "bg-red-500/10 text-red-600 border-red-500/20",
};

export default function PipelineView({ quotes, onDeliverySet }) {
  const active = quotes.filter((q) => q.status !== "rejected" && q.status !== "completed");
  const completed = quotes.filter((q) => q.status === "completed");

  const setDelivery = async (quoteId, date) => {
    try {
      await api.put(`/admin/quotes/${quoteId}/delivery`, { expected_delivery: date });
      onDeliverySet();
    } catch (e) {
      console.error("Failed to set delivery date:", e);
    }
  };

  const isOverdue = (q) => {
    if (!q.expected_delivery || q.status === "completed") return false;
    return new Date(q.expected_delivery) < new Date();
  };

  const daysLeft = (date) => {
    return Math.ceil((new Date(date) - new Date()) / 86400000);
  };

  return (
    <div>
      {/* Summary row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[
          { l: "Active Projects", v: active.length, icon: TrendingUp, color: "text-[#2060EE]" },
          { l: "Completed", v: completed.length, icon: Check, color: "text-emerald-600" },
          { l: "No Delivery Date", v: active.filter(q => !q.expected_delivery).length, icon: AlertCircle, color: "text-amber-600" },
          { l: "Overdue", v: active.filter(isOverdue).length, icon: Clock, color: "text-red-600" },
        ].map((s) => (
          <div key={s.l} className="glass-card rounded-xl p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#F2F2F5] grid place-items-center flex-shrink-0">
              <s.icon className={`w-4 h-4 ${s.color}`} />
            </div>
            <div>
              <p className="text-xs text-[#5A5A72]">{s.l}</p>
              <p className={`font-display font-black text-2xl ${s.color}`}>{s.v}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Active pipeline */}
      <h3 className="font-display font-bold text-lg text-[#0E0E1A] mb-4">Active Projects</h3>
      {active.length === 0 ? (
        <div className="glass-card rounded-xl p-8 text-center text-[#5A5A72] mb-8">No active projects right now.</div>
      ) : (
        <div className="space-y-3 mb-10">
          {active.map((q) => {
            const overdue = isOverdue(q);
            const days = q.expected_delivery ? daysLeft(q.expected_delivery) : null;
            return (
              <motion.div
                key={q.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                data-testid={`pipeline-card-${q.id}`}
                className={`glass-card rounded-xl p-5 border ${overdue ? "border-red-300" : "border-transparent"}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full border ${STATUS_COLORS[q.status]}`}>
                        {q.status.replace("_", " ")}
                      </span>
                      {overdue && <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-100 text-red-600 border border-red-200">Overdue</span>}
                    </div>
                    <p className="font-semibold text-[#0E0E1A] truncate">{q.project_title || q.service_type}</p>
                    <p className="text-sm text-[#5A5A72]">{q.contact_name} · {q.contact_email}</p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-[#6B7280]">
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />Started {new Date(q.created_at).toLocaleDateString()}</span>
                      {q.expected_delivery && (
                        <span className={`flex items-center gap-1 font-medium ${overdue ? "text-red-600" : days <= 3 ? "text-amber-600" : "text-[#2060EE]"}`}>
                          <Package className="w-3 h-3" />
                          {overdue ? `Overdue by ${Math.abs(days)}d` : `${days}d left`} (due {new Date(q.expected_delivery).toLocaleDateString()})
                        </span>
                      )}
                    </div>
                    {/* Progress bar */}
                    <div className="mt-3 flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-[#F2F2F5] rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-[#00D4FF] to-[#2060EE] rounded-full transition-all" style={{ width: `${q.progress || 0}%` }} />
                      </div>
                      <span className="text-xs font-mono text-[#5A5A72]">{q.progress || 0}%</span>
                    </div>
                  </div>
                  {/* Set delivery date */}
                  <div className="flex flex-col items-end gap-2">
                    <label className="text-[10px] font-mono uppercase tracking-wider text-[#5A5A72]">Delivery date</label>
                    <input
                      type="date"
                      defaultValue={q.expected_delivery ? q.expected_delivery.split("T")[0] : ""}
                      onChange={(e) => setDelivery(q.id, e.target.value)}
                      data-testid={`delivery-date-${q.id}`}
                      className="px-3 py-1.5 rounded-lg bg-white border border-[rgba(14,14,26,0.12)] text-xs text-[#0E0E1A] focus:border-[#2060EE]/60 outline-none cursor-pointer"
                    />
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Completed */}
      <h3 className="font-display font-bold text-lg text-[#0E0E1A] mb-4">Completed Deliveries</h3>
      {completed.length === 0 ? (
        <div className="glass-card rounded-xl p-8 text-center text-[#5A5A72]">No completed projects yet.</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {completed.map((q) => (
            <div key={q.id} data-testid={`pipeline-done-${q.id}`} className="glass-card rounded-xl p-4 border border-emerald-200/40">
              <p className="font-semibold text-[#0E0E1A] truncate">{q.project_title || q.service_type}</p>
              <p className="text-sm text-[#5A5A72]">{q.contact_name}</p>
              <div className="flex items-center gap-2 mt-2 text-xs text-emerald-600 font-medium">
                <Check className="w-3 h-3" />
                Delivered {q.expected_delivery ? `by ${new Date(q.expected_delivery).toLocaleDateString()}` : ""}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
